import { toast } from '../components/Toast';
import { getMpesaPaymentStatus, initiateMpesaPayment } from '../api/payment.api';
import { AuthContext } from '../context/AuthContext';

const formatKES = (amount) => 'KES ' + Number(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });

const MAX_POLLS = 12;
const POLL_INTERVAL = 5000; // 5 seconds

export default function PaymentStatus() {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated } = useContext(AuthContext);

    const [status, setStatus] = useState('waiting'); // waiting | paid | failed | timeout
    const [pollCount, setPollCount] = useState(0);
    const [orderData, setOrderData] = useState(null);
    const [retrying, setRetrying] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(false);

    const pollingRef = useRef(null);
    const pollCountRef = useRef(0);

    useEffect(() => {
        document.title = 'Payment Status | Fone Express';
    }, []);

    const checkPaymentStatus = useCallback(async () => {
        try {
            const res = await getMpesaPaymentStatus(orderId);
            if (res.data?.isOk && res.data?.data) {
                const data = res.data.data;
                setOrderData(data);

                if (data.paymentStatus === 'paid' || data.status === 'paid') {
                    setStatus('paid');
                    stopPolling();
                    return;
                }

                if (data.paymentStatus === 'failed' || data.status === 'failed') {
                    setStatus('failed');
                    stopPolling();
                    return;
                }
            }
        } catch (err) {
            console.error('Error checking payment status:', err);
        }

        pollCountRef.current += 1;
        setPollCount(pollCountRef.current);

        if (pollCountRef.current >= MAX_POLLS) {
            setStatus('timeout');
            stopPolling();
        }
    }, [orderId]);

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    const startPolling = useCallback(() => {
        stopPolling();
        pollCountRef.current = 0;
        setPollCount(0);
        setStatus('waiting');

        // Check immediately, then poll
        checkPaymentStatus();
        pollingRef.current = setInterval(checkPaymentStatus, POLL_INTERVAL);
    }, [checkPaymentStatus, stopPolling]);

    // Start polling on mount
    useEffect(() => {
        if (orderId) {
            startPolling();
        }
        return () => stopPolling();
    }, [orderId, startPolling, stopPolling]);

    const handleRetryPayment = async () => {
        if (retrying) return;
        setRetrying(true);
        try {
            const res = await initiateMpesaPayment({ orderId });
            if (res.data?.isOk) {
                toast.success('Payment prompt sent to your phone');
                startPolling();
            } else {
                toast.error(res.data?.message || 'Failed to retry payment');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to initiate payment');
        } finally {
            setRetrying(false);
        }
    };

    const handleCheckStatus = async () => {
        if (checkingStatus) return;
        setCheckingStatus(true);
        try {
            const res = await getMpesaPaymentStatus(orderId);
            if (res.data?.isOk && res.data?.data) {
                const data = res.data.data;
                setOrderData(data);

                if (data.paymentStatus === 'paid' || data.status === 'paid') {
                    setStatus('paid');
                    toast.success('Payment confirmed!');
                } else if (data.paymentStatus === 'failed' || data.status === 'failed') {
                    setStatus('failed');
                    toast.error('Payment failed');
                } else {
                    toast.info('Payment is still being processed');
                }
            }
        } catch (err) {
            toast.error('Unable to check payment status');
        } finally {
            setCheckingStatus(false);
        }
    };

    const handleViewOrder = () => {
        if (isAuthenticated && orderData?.orderNumber) {
            navigate(`/account/orders/${orderData.orderNumber}`);
        } else if (orderData?.trackingToken) {
            navigate(`/track-order?token=${orderData.trackingToken}`);
        } else {
            navigate('/');
        }
    };

    return (
        <div className="min-h-screen bg-muted pt-[10px]">
            <div className="max-w-[600px] mx-auto px-4 py-12">
                {/* Waiting State */}
                {status === 'waiting' && (
                    <div className="bg-card rounded-lg border border-border p-8 text-center">
                        {/* Animated pulse */}
                        <div className="relative w-24 h-24 mx-auto mb-6">
                            <div className="absolute inset-0 bg-primary/10 rounded-full animate-ping"></div>
                            <div className="absolute inset-2 bg-primary/20 rounded-full animate-pulse"></div>
                            <div className="absolute inset-4 bg-card rounded-full flex items-center justify-center shadow-sm">
                                <i className="ri-smartphone-line text-3xl text-primary"></i>
                            </div>
                        </div>

                        <h2 className="text-xl font-bold text-foreground mb-2">Waiting for M-PESA Payment</h2>
                        <p className="text-muted-foreground mb-6">
                            We've sent an M-PESA payment prompt to your phone. Please enter your PIN to complete the payment.
                        </p>

                        {/* Progress indicator */}
                        <div className="mb-6">
                            <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                                <div
                                    className="bg-primary h-full rounded-full transition-all duration-500"
                                    style={{ width: `${(pollCount / MAX_POLLS) * 100}%` }}
                                ></div>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                Checking payment status... ({Math.max(0, (MAX_POLLS - pollCount) * 5)}s remaining)
                            </p>
                        </div>

                        <div className="p-3 bg-muted rounded-lg">
                            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                                <div className="w-4 h-4 border-2 border-border border-t-primary rounded-full animate-spin"></div>
                                Waiting for confirmation...
                            </div>
                        </div>
                    </div>
                )}

                {/* Success State */}
                {status === 'paid' && (
                    <div className="bg-card rounded-lg border border-border p-8 text-center">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <i className="ri-check-line text-4xl text-green-600"></i>
                        </div>

                        <h2 className="text-xl font-bold text-foreground mb-2">Payment Successful!</h2>
                        <p className="text-muted-foreground mb-6">Your payment has been confirmed. Thank you for your order!</p>

                        {orderData && (
                            <div className="bg-muted rounded-lg p-4 mb-6 text-left space-y-2">
                                {orderData.orderNumber && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Order Number</span>
                                        <span className="text-foreground font-medium">{orderData.orderNumber}</span>
                                    </div>
                                )}
                                {orderData.receiptNumber && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Receipt Number</span>
                                        <span className="text-foreground font-medium">{orderData.receiptNumber}</span>
                                    </div>
                                )}
                                {orderData.amount && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Amount Paid</span>
                                        <span className="text-foreground font-bold">{formatKES(orderData.amount)}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                onClick={handleViewOrder}
                                className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:brightness-110 transition-all"
                            >
                                View Order
                            </button>
                            <button
                                onClick={() => navigate('/products')}
                                className="px-6 py-3 border border-border text-foreground rounded-lg font-semibold hover:bg-muted transition-colors"
                            >
                                Continue Shopping
                            </button>
                        </div>
                    </div>
                )}

                {/* Failed State */}
                {status === 'failed' && (
                    <div className="bg-card rounded-lg border border-border p-8 text-center">
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <i className="ri-close-line text-4xl text-red-600"></i>
                        </div>

                        <h2 className="text-xl font-bold text-foreground mb-2">Payment Failed</h2>
                        <p className="text-muted-foreground mb-6">
                            The payment could not be completed. This may be due to insufficient funds, an incorrect PIN, or a cancelled transaction.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                onClick={handleRetryPayment}
                                disabled={retrying}
                                className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:brightness-110 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {retrying ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Retrying...
                                    </>
                                ) : (
                                    <>
                                        <i className="ri-refresh-line"></i>
                                        Retry Payment
                                    </>
                                )}
                            </button>
                            <Link
                                to="/cart"
                                className="px-6 py-3 border border-border text-foreground rounded-lg font-semibold hover:bg-muted transition-colors text-center"
                            >
                                Back to Cart
                            </Link>
                        </div>
                    </div>
                )}

                {/* Timeout State */}
                {status === 'timeout' && (
                    <div className="bg-card rounded-lg border border-border p-8 text-center">
                        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <i className="ri-time-line text-4xl text-amber-600"></i>
                        </div>

                        <h2 className="text-xl font-bold text-foreground mb-2">Payment Taking Longer Than Expected</h2>
                        <p className="text-muted-foreground mb-4">
                            We haven't received confirmation yet. Don't worry -- if you completed the payment, we'll update your order once it's confirmed.
                        </p>

                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                            <p className="text-sm text-amber-800">
                                <i className="ri-information-line mr-1"></i>
                                If you entered your M-PESA PIN, your payment may still be processing. Please check your M-PESA messages for confirmation.
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <button
                                onClick={handleCheckStatus}
                                disabled={checkingStatus}
                                className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:brightness-110 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {checkingStatus ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                        Checking...
                                    </>
                                ) : (
                                    <>
                                        <i className="ri-refresh-line"></i>
                                        Check Status
                                    </>
                                )}
                            </button>
                            <button
                                onClick={handleRetryPayment}
                                disabled={retrying}
                                className="px-6 py-3 border border-border text-foreground rounded-lg font-semibold hover:bg-muted transition-colors flex items-center justify-center gap-2"
                            >
                                {retrying ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-border border-t-primary rounded-full animate-spin"></div>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <i className="ri-send-plane-line"></i>
                                        Resend Payment Prompt
                                    </>
                                )}
                            </button>
                        </div>

                        <div className="mt-4">
                            <Link
                                to="/"
                                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                            >
                                Return to Home
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
